/**
 * Payment & autopay, on the customer's card.
 *
 * Owner, Oct 2 2026: "save payment for the client where I can auto bill them."
 * The office never types a card. The customer saves one on Stripe's page from
 * their own link; here the office sees what is on file, flips autopay for them
 * (refused until a card is there), texts them the link that opens their page
 * at the save-a-card step, and sees whether the Settings switch that charges
 * cards at all is on (server: routes/billing /customers/:id/payment).
 */
import React from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { Link } from 'react-router-dom';
import { CreditCard, MessageSquare } from 'lucide-react';
import { toast } from 'react-toastify';
import { api } from '../services/api';
import { useReadOnly } from '../hooks/useReadOnly';

interface PaymentState {
  stripe: 'not_configured' | 'test' | 'live';
  practice: boolean;
  card: { on_file: boolean; brand: string | null; last4: string | null; saved_at: string | null; error?: string };
  autopay_enabled: boolean;
  billing_cadence?: 'per_visit' | 'month_end';   // billed after each visit, or at month end (server: services/billCadence)
  billing_cadence_words?: string;
  late_fee_exempt?: boolean;    // no late fees or interest for this customer (server: services/lateFees)
  switch_on: boolean;    // the Settings switch "Auto-charge autopay customers", as set
  charge_on: boolean;    // whether a card would actually be charged — practice and no Stripe stop it too
  sms_on: boolean;
  phone: string | null;
  sms_opt_out: boolean;
}

/**
 * Why no card is charged, by cause, or null when one would be. The Settings
 * link is offered only when the switch is the cause — it used to send him to
 * Settings in practice, where the switch was already on.
 */
export function noChargeWords(pay: Pick<PaymentState, 'charge_on' | 'switch_on' | 'practice' | 'stripe'>): { words: string; settings: boolean } | null {
  if (pay.charge_on) return null;
  const sw = `The Settings switch "Auto-charge autopay customers" is ${pay.switch_on ? 'on' : 'off'}.`;
  if (pay.practice) return { words: `Practice never charges a card. ${sw}`, settings: !pay.switch_on };
  if (pay.stripe === 'not_configured') return { words: `Charging a card needs Stripe, which is not set up here yet. ${sw}`, settings: !pay.switch_on };
  return { words: 'Auto-charge autopay customers is off in Settings, so no card is charged until it is on.', settings: true };
}

const BRANDS: Record<string, string> = { visa: 'Visa', mastercard: 'Mastercard', amex: 'Amex', discover: 'Discover' };
const brandWord = (b: string | null) => (b ? BRANDS[b] || `${b.charAt(0).toUpperCase()}${b.slice(1)}` : 'Card');
const savedWord = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';

export default function PaymentAutopayBlock({ customerId }: { customerId: number }) {
  const readOnly = useReadOnly();   // view-only login: grey out what can only ever be refused
  const qc = useQueryClient();
  const { data } = useQuery(['customer-payment', customerId],
    () => api.get(`/billing/customers/${customerId}/payment`).then(r => r.data as PaymentState),
    { retry: false });

  const toggle = useMutation((on: boolean) => api.put(`/billing/customers/${customerId}/autopay`, { on }).then(r => r.data), {
    onSuccess: (d: any) => {
      toast.success(d?.message || 'Saved');
      qc.invalidateQueries(['customer-payment', customerId]); qc.invalidateQueries('billing-review');
    },
    onError: (e: any) => { toast.error(e?.response?.data?.message || 'Could not change autopay'); },
  });
  const exempt = useMutation((on: boolean) => api.put(`/billing/customers/${customerId}/late-fee-exempt`, { exempt: on }).then(r => r.data), {
    onSuccess: (d: any) => { toast.success(d?.message || 'Saved'); qc.invalidateQueries(['customer-payment', customerId]); },
    onError: (e: any) => { toast.error(e?.response?.data?.message || 'Could not save that'); },
  });
  const cadence = useMutation((c: 'per_visit' | 'month_end') => api.put(`/billing/customers/${customerId}/cadence`, { cadence: c }).then(r => r.data), {
    onSuccess: (d: any) => {
      toast.success(d?.message || 'Saved');
      qc.invalidateQueries(['customer-payment', customerId]); qc.invalidateQueries('billing-review');
    },
    onError: (e: any) => { toast.error(e?.response?.data?.message || 'Could not change how they are billed'); },
  });
  const textLink = useMutation(() => api.post(`/billing/customers/${customerId}/card-link`).then(r => r.data), {
    onSuccess: (d: any) => {
      if (d?.sent) { toast.success(d.message || 'Texted'); }
      else if (d?.draft) {
        // No way to text from here — hand him the words to send himself.
        toast.info(d.message || 'Copy this text');
        window.prompt('Copy this text to send them:', d.draft.body);
      } else { toast.info(d?.message || 'Done'); }
    },
    onError: (e: any) => { toast.error(e?.response?.data?.message || 'Could not send that text'); },
  });

  if (!data) return null;
  const { card, stripe } = data;
  const noStripe = stripe === 'not_configured';
  const noCharge = noChargeWords(data);

  return (
    <div className="mt-3 rounded-lg border border-gray-200 bg-white px-3 py-2.5">
      <p className="text-xs font-bold uppercase tracking-wide text-gray-600 mb-1.5">Payment &amp; autopay</p>

      {/* The card: what Stripe holds. Never a number here. */}
      <div className="flex items-start gap-2">
        <CreditCard className={`h-4 w-4 mt-0.5 flex-shrink-0 ${card.on_file ? 'text-green-600' : 'text-gray-400'}`} />
        <div className="min-w-0 text-sm text-gray-800">
          {card.on_file ? (
            <p>
              <span className="font-semibold">{brandWord(card.brand)}{card.last4 ? ` ···· ${card.last4}` : ' on file'}</span>
              {card.saved_at && <span className="text-gray-500"> · saved {savedWord(card.saved_at)}</span>}
              {stripe === 'test' && <span className="text-amber-700"> · Stripe test mode</span>}
            </p>
          ) : (
            <p className="text-gray-700">No card on file.</p>
          )}
          {card.error && <p className="text-xs text-amber-800">{card.error}</p>}
          {noStripe && (
            <p className="text-xs text-gray-500">
              Stripe is not set up here{data.practice ? ' (practice)' : ''}, so no card can be saved yet.
            </p>
          )}
        </div>
      </div>

      {/* No late fees for this customer (owner, Nov 1 2026; services/lateFees). */}
      <div className="mt-2 flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-gray-900">Late fees {data.late_fee_exempt ? 'never' : 'apply'}</p>
          <p className="text-xs text-gray-600">{data.late_fee_exempt ? 'Marked exempt: the daily run leaves their bills alone.' : 'The daily run adds the late fee and interest to their overdue bills like everyone else.'}</p>
        </div>
        <button type="button" disabled={readOnly || exempt.isLoading} onClick={() => exempt.mutate(!data.late_fee_exempt)}
          className={`shrink-0 text-xs font-semibold rounded-lg border px-2.5 py-1.5 ${data.late_fee_exempt ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-700 border-gray-300'} disabled:opacity-60`}>
          {data.late_fee_exempt ? 'Exempt ✓' : 'Make exempt'}
        </button>
      </div>

      {/* Billed after each visit or at month end — their pick on the bid (owner, Oct 27 2026), changeable here. */}
      <div className="mt-2 flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-gray-900">Billed {(data.billing_cadence || 'month_end') === 'per_visit' ? 'after each visit' : 'at month end'}</p>
          <p className="text-xs text-gray-600">
            {(data.billing_cadence || 'month_end') === 'per_visit'
              ? 'A finished visit drafts its own bill, waiting on Billing → Review & send. A lawn plan or other monthly charge stays on the month-end bill.'
              : "One bill at month end for the month's visits."}
          </p>
        </div>
        <div className="shrink-0 inline-flex rounded-lg border border-gray-300 overflow-hidden text-xs font-semibold">
          {([['per_visit', 'Each visit'], ['month_end', 'Month end']] as const).map(([key, label]) => (
            <button key={key} type="button" disabled={readOnly || cadence.isLoading || (data.billing_cadence || 'month_end') === key}
              onClick={() => cadence.mutate(key)}
              className={`px-2.5 py-1.5 ${(data.billing_cadence || 'month_end') === key ? 'bg-gray-900 text-white' : 'bg-white text-gray-700'} disabled:opacity-100`}>
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Autopay: the office's switch for this one customer. */}
      <div className="mt-2 flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-gray-900">Autopay {data.autopay_enabled ? 'is on' : 'is off'}</p>
          <p className="text-xs text-gray-600">
            {data.autopay_enabled
              ? card.on_file ? "Their card pays each month's bill when you send it; they get a receipt." : 'But there is no card to charge — nothing is charged until they save one.'
              : card.on_file ? 'They get the bill and pay it themselves. Turn it on and they get a text saying their card now pays each bill, with the link to turn it off.'
              : 'They get the bill and pay it themselves.'}
          </p>
        </div>
        <button type="button" disabled={readOnly || toggle.isLoading}
          onClick={() => toggle.mutate(!data.autopay_enabled)}
          title={!data.autopay_enabled && !card.on_file ? 'They need to save a card first' : undefined}
          className={`shrink-0 px-3 py-1.5 rounded-lg text-xs font-semibold border disabled:opacity-50 ${
            data.autopay_enabled ? 'border-gray-300 text-gray-700 bg-white' : 'bg-gray-900 text-white border-gray-900'}`}>
          {toggle.isLoading ? '…' : data.autopay_enabled ? 'Turn off' : 'Turn on'}
        </button>
      </div>

      {/* The link that opens their page at "Save a credit card". */}
      <div className="mt-2">
        <button type="button" disabled={readOnly || textLink.isLoading} onClick={() => textLink.mutate()}
          className="press inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg bg-brand-600 text-black font-semibold disabled:opacity-50">
          <MessageSquare className="h-3.5 w-3.5" /> {textLink.isLoading ? 'Sending…' : 'Text them a link to save a card'}
        </button>
        <p className="text-[11px] text-gray-500 mt-1">
          Opens their page at the save-a-card step. They type the card on Stripe's page — never here.
          {data.sms_opt_out ? ' They have opted out of texts, so you get the words to send yourself.' : !data.phone ? ' No phone on file — you get the words to send yourself.' : ''}
        </p>
      </div>

      {/* Whether any card is charged at all: the switch in Settings — and Stripe, and not practice. */}
      <p className={`mt-2 text-xs ${noCharge ? 'text-amber-800' : 'text-green-800'}`}>
        {noCharge
          ? <>{noCharge.words}{noCharge.settings && <> <Link to="/settings#billing-automation" className="underline font-semibold">Settings → Billing</Link></>}</>
          : 'Auto-charge autopay customers is on in Settings — their card is charged when the bill is sent.'}
      </p>
    </div>
  );
}
